"use client"

import { useState, useEffect } from "react"
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  Switch,
  FormControlLabel,
  Box,
  Typography,
  IconButton,
  CircularProgress,
} from "@mui/material"
import { DatePicker } from "@mui/x-date-pickers/DatePicker"
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns"
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider"
import { Close as CloseIcon, CloudUpload as UploadIcon } from "@mui/icons-material"
import FilePreview from "./FilePreview"

export default function EditPostDialog({ open, onClose, post, onSave, category }) {
  const [formData, setFormData] = useState(post)
  const [files, setFiles] = useState({
    photo: null,
    picture_1: null,
    picture_2: null,
  })
  const [changedFiles, setChangedFiles] = useState({
    photo: false,
    picture_1: false,
    picture_2: false,
  })
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    setFormData(post)
    // Initialize file previews if post has existing images
    setFiles({
      photo: post.photo || null,
      picture_1: post.picture_1 || null,
      picture_2: post.picture_2 || null,
    })
    setChangedFiles({
      photo: false,
      picture_1: false,
      picture_2: false,
    })
  }, [post])

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }))
  }

  const handleDateChange = (field) => (date) => {
    setFormData((prev) => ({
      ...prev,
      [field]: date?.toISOString() || null,
    }))
  }

  const handleFileChange = (e) => {
    const { name, files: uploadedFiles } = e.target
    if (uploadedFiles.length > 0) {
      setFiles((prev) => ({
        ...prev,
        [name]: uploadedFiles[0],
      }))
      setChangedFiles((prev) => ({
        ...prev,
        [name]: true,
      }))
    }
  }

  const removeFile = (name) => {
    setFiles((prev) => ({
      ...prev,
      [name]: null,
    }))
    setChangedFiles((prev) => ({
      ...prev,
      [name]: true,
    }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    try {
      const formDataToSend = new FormData()
      formDataToSend.append("subject_line", formData.subject_line)
      formDataToSend.append("input_text", formData.input_text)
      formDataToSend.append("feed_show_status", formData.feed_show_status)
      formDataToSend.append("startDay", formData.startDay)
      formDataToSend.append("endDay", formData.endDay)
      formDataToSend.append("welcomeStartDay", formData.startDay)
      formDataToSend.append("welcomeEndDay", formData.endDay)

      // Only append changed files
      Object.entries(files).forEach(([key, file]) => {
        if (changedFiles[key] && file) {
          formDataToSend.append(key, file)
        }
      })

      const response = await fetch(`${process.env.REACT_APP_API_URL}/posts/update_post/${post._id}`, {
        method: "PUT",
        body: formDataToSend,
      })

      if (response.ok) {
        const updatedPost = await response.json()
        onSave(updatedPost)
        onClose()
      } else {
        throw new Error("Failed to update post")
      }
    } catch (error) {
      console.error("Error updating post:", error)
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography variant="h6">Edit {category}</Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Stack spacing={3}>
            <TextField
              label="Subject Line"
              name="subject_line"
              value={formData.subject_line || ""}
              onChange={handleChange}
              fullWidth
              required
            />

            <TextField
              label="Description"
              name="input_text"
              value={formData.input_text || ""}
              onChange={handleChange}
              multiline
              rows={4}
              fullWidth
              required
            />

            <Box>
              <Typography variant="subtitle1" gutterBottom>
                Media Files
              </Typography>
              <Stack direction="row" spacing={2} mb={2}>
                <Button variant="outlined" component="label" startIcon={<UploadIcon />}>
                  Main Photo
                  <input type="file" hidden name="photo" onChange={handleFileChange}accept="image/*,video/*,audio/*,application/pdf" />
                </Button>
                <Button variant="outlined" component="label" startIcon={<UploadIcon />}>
                  Additional Photo 1
                  <input
                    type="file"
                    hidden
                    name="picture_1"
                    onChange={handleFileChange}
                   accept="image/*,video/*,audio/*,application/pdf"
                  />
                </Button>
                <Button variant="outlined" component="label" startIcon={<UploadIcon />}>
                  Additional Photo 2
                  <input
                    type="file"
                    hidden
                    name="picture_2"
                    onChange={handleFileChange}
                   accept="image/*,video/*,audio/*,application/pdf"
                  />
                </Button>
              </Stack>
              <Stack direction="row" spacing={2}>
                {Object.entries(files).map(
                  ([key, file]) => file && <Box
                    sx={{
                      position: "relative",
                      width: 100,
                      height: 100,
                      border: "1px solid #ccc",
                      borderRadius: 1,
                      overflow: "hidden",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {file.type == 'image' && (
                      <img
                        src={file.url || "/placeholder.svg"}
                        alt={file.type}
                        style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "cover" }}
                      />
                    )}
                    {file.type == 'video' && (
                      <video src={file.url} style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "cover" }} />
                    )}
                    {file.type == 'audio' && <audio src={file.url} controls style={{ maxWidth: "100%" }} />}
                    {!(file.type == 'image' || file.type == 'video' || file.type == 'audio') && (
                      <Typography variant="body2" align="center">
                        {file.name}
                      </Typography>
                    )}
                    <IconButton
                      size="small"
                      onClick={removeFile}
                      sx={{
                        position: "absolute",
                        top: 0,
                        right: 0,
                        bgcolor: "rgba(255, 255, 255, 0.7)",
                        "&:hover": {
                          bgcolor: "rgba(255, 255, 255, 0.9)",
                        },
                      }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>,
                )}
              </Stack>
            </Box>

            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Stack direction="row" spacing={2}>
                <DatePicker
                  label="Start Date"
                  value={formData.startDay ? new Date(formData.startDay) : null}
                  onChange={handleDateChange("startDay")}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                    },
                  }}
                />
                <DatePicker
                  label="End Date"
                  value={formData.endDay ? new Date(formData.endDay) : null}
                  onChange={handleDateChange("endDay")}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                    },
                  }}
                />
              </Stack>
              <Stack direction="row" spacing={2}>
                <DatePicker
                  label="Welcome Start Date"
                  value={formData.welcomeStartDay ? new Date(formData.welcomeStartDay) : null}
                  onChange={handleDateChange('welcomeStartDay')}
                  slotProps={{
                    textField: {
                      fullWidth: true
                    }
                  }}
                />
                <DatePicker
                  label="Welcome End Date"
                  value={formData.welcomeEndDay ? new Date(formData.welcomeEndDay) : null}
                  onChange={handleDateChange('welcomeEndDay')}
                  slotProps={{
                    textField: {
                      fullWidth: true
                    }
                  }}
                />
              </Stack>
            </LocalizationProvider>

            <FormControlLabel
              control={<Switch checked={formData.feed_show_status} onChange={handleChange} name="feed_show_status" />}
              label="Show in Feed"
            />
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            startIcon={loading && <CircularProgress size={20} />}
          >
            Save Changes
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}

